import {
  Badge,
  Box,
  Text,
  Divider,
  ToggleButton,
  Card,
  CloseButton,
  Popover,
} from '@wix/design-system';
import { FunctionComponent, useEffect, useState } from 'react';
import css from './index.module.scss';
import { ThumbsUp, ThumbsDown } from '@wix/wix-ui-icons-common';
import { ArticleHelpfulState } from '@wix/answers-api';
import { ArticleSmallIcon } from '../../../icons/ArticleSmallIcon';
import { GenAiArticleLink } from '../../../types';
import Link from 'next/link';
import { DATA_HOOKS } from '../../../dataHooks';
import { useTranslation } from '@wix/wix-i18n-config';

type GenAnswerFooterProps = {
  moreInfoArticles: GenAiArticleLink[];
};

enum unhelpfulFeedbackTypes {
  WRONG = 'wrong',
  IRRELEVANT = 'irrelevant',
  CONFUSING = 'confusing',
  HARD = 'hard',
  OUTDATED = 'outdated',
}

export const GenAnswerFooter: FunctionComponent<GenAnswerFooterProps> = ({
  moreInfoArticles
}) => {
  const [helpfulState, setHelpfulState] = useState<ArticleHelpfulState>(ArticleHelpfulState.NONE);
  const [responded, setResponded] = useState<boolean>(false);
  const [reasonResponded, setReasonResponded] = useState<boolean>(false);
  const [popoverShown, setPopoverShown] = useState<boolean>(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (responded || reasonResponded) {
      setPopoverShown(true);
    }
  }, [responded, reasonResponded]);

  const toggleHelpfulState = (state: ArticleHelpfulState) => {
    if (responded) {
      if (helpfulState === state) {
        unSelectSelf();
      } else {
        setPopoverShown(true);
        setHelpfulState(state);
      }
    } else {
      setHelpfulState(state);
      setResponded(true);
    }
    setReasonResponded(false);
  };

  const unSelectSelf = () => {
    setHelpfulState(ArticleHelpfulState.NONE);
    setResponded(false);
  };

  return (
    <Box direction="vertical" dataHook={DATA_HOOKS.GEN_ANSWER_FOOTER}>
      <Box direction="horizontal" className={css.nextGenAnswerFooterInner}>
        <Box className={css.readMoreArticlesWrapper}>
          <Text className={css.readMore} size='small'>
            {t('search-results-page.gen-answer.read-more')}
          </Text>

          {moreInfoArticles.map((articleLink, index) => (
            <Link key={index} href={articleLink.articleURL} target="_blank" className={css.readMoreArticleLink}>
              <ArticleSmallIcon />
              <Text
                dataHook={DATA_HOOKS.GEN_ANSWER_SOURCE_ARTICLE}
                size="tiny"
                className={css.name}
              >
                {articleLink.articleName}{index + 1 < moreInfoArticles.length && ','}
              </Text>
            </Link>
          ))}
        </Box>
        <Box className={css.genAnswerFeedback}>
          <Divider className={css.verticalDivider} direction="vertical" />
          <Box className={css.feedbackOptionsWrapper} direction="vertical">
            <Box className={css.feedbackOptions} dataHook={DATA_HOOKS.GEN_ANSWER_VOTING}>
              <ToggleButton selected={helpfulState === ArticleHelpfulState.HELPFUL}
                dataHook={DATA_HOOKS.GEN_ANSWER_HELPFUL}
                shape="round" border={true} onClick={() => toggleHelpfulState(ArticleHelpfulState.HELPFUL)}
                labelValue={t('search-results-page.gen-answer.helpful')}
                labelPlacement="tooltip"
                tooltipProps={{ disabled: popoverShown }}>
                <ThumbsUp></ThumbsUp>
              </ToggleButton>
              <Popover
                placement='top'
                appendTo="window"
                shown={popoverShown}
                onShow={() => {
                  setTimeout(() => {
                    setPopoverShown(false);
                  }, 2000);
                }
                }
              >
                <Popover.Element>
                  <ToggleButton
                    selected={helpfulState === ArticleHelpfulState.NOT_HELPFUL}
                    dataHook={DATA_HOOKS.GEN_ANSWER_NOT_HELPFUL}
                    shape="round" border={true}
                    onClick={() => toggleHelpfulState(ArticleHelpfulState.NOT_HELPFUL)}
                    labelValue={t('search-results-page.gen-answer.unhelpful')}
                    labelPlacement="tooltip"
                    tooltipProps={{ disabled: popoverShown }}>
                    <ThumbsDown></ThumbsDown>
                  </ToggleButton>
                </Popover.Element>
                <Popover.Content>
                  <Box className={css.popoverFeedback} padding="10px">{helpfulState === ArticleHelpfulState.NOT_HELPFUL ?
                    t('search-results-page.gen-answer.improve') : t('search-results-page.gen-answer.helped')}</Box>
                </Popover.Content>
              </Popover>
            </Box>
          </Box>
        </Box>
      </Box >
      {!reasonResponded && helpfulState === ArticleHelpfulState.NOT_HELPFUL &&
        <Card
          className={css.notHelpfulFeedbackCard}
          dataHook={DATA_HOOKS.GEN_ANSWER_UNHELPFUL_REASONS}
          controls={<CloseButton size="large" skin="dark"
            onClick={() => setReasonResponded(true)} />}>
          <Text className={css.title} weight='normal'>
            {t('search-results-page.gen-answer.why-unhelpful')} <span className={css.optional}>(optional)</span>
          </Text>
          <Box direction="horizontal" gap='9px' >
            {Object.values(unhelpfulFeedbackTypes).map((type, index) => (
              <Badge
                className={css.badge}
                skin="neutralStandard" type="outlined"
                onClick={() =>
                  setReasonResponded(true)
                } uppercase={false} key={index}
                dataHook={DATA_HOOKS.GEN_ANSWER_UNHELPFUL_BADGE}>
                {t(`search-results-page.gen-answer.reason.${type}`)}
              </Badge>
            ))}
          </Box>
        </Card>
      }
    </Box >
  );
};
